import * as Sentry from '@sentry/browser';
import { SeverityLevel } from '@sentry/types';
import { AnyException, Extras, SentryScopeConfig } from './types';
import get from 'lodash/get';

const ERROR_404 = 404;
const ERROR_422 = 422;
const VAT_DEFERRAL_END = '/vat-deferral/';
const DATA_CHECK_END = '/data-check/';
/**
 * Singleton to log the issues across the app.
 */
export class ErrorTracker {
  public static level: Record<string, SeverityLevel> = {
    Debug: 'debug',
    Log: 'log',
    Error: 'error',
    Info: 'info',
    Warning: 'warning',
    Fatal: 'fatal'
  };

  /**
   * Init the error tracker.
   *
   * @param {string} dsn
   * @param {string} environment
   * @param {string|undefined} release
   */
  public static init(dsn: string, environment: string, release?: string): void {
    Sentry.init({
      beforeSend(event, hint) {
        const apiPath = get(hint, 'originalException.config.path', '');
        const apiResponse = get(hint, 'originalException.response.status');
        if (
          (apiPath.endsWith(VAT_DEFERRAL_END) && apiResponse === ERROR_404) ||
          (apiPath.endsWith(DATA_CHECK_END) && apiResponse === ERROR_422)
        ) {
          return null;
        }
        return event;
      },
      dsn,
      environment,
      release
    });
  }

  /**
   * Configures the scope.
   *
   * @param {SentryScopeConfig} config - Scope configuration.
   */
  public static configureScope({ userId }: SentryScopeConfig): void {
    Sentry.getCurrentScope().setUser({
      id: userId
    });
  }

  /**
   * Cleans the current scope.
   */
  public static cleanScope(): void {
    Sentry.getCurrentScope().setUser(null);
  }

  /**
   * Logs a message.
   *
   * @param {string} message
   * @param { SeverityLevel} level
   */
  public static captureMessage(message: string, level: SeverityLevel): void {
    Sentry.captureMessage(message, level);
  }

  /**
   * Logs an exception.
   *
   * @param {AnyException} exception
   * @param {Extras} extraInfo
   */
  public static captureException(exception: AnyException, extraInfo?: Extras): void {
    Sentry.withScope((scope: Sentry.Scope) => {
      extraInfo && scope.setExtras(extraInfo);
      Sentry.captureException(exception);
    });
  }

  /**
   * Logs an event.
   *
   * @param {Sentry.Event} event
   */
  public static captureEvent(event: Sentry.Event): void {
    Sentry.captureEvent(event);
  }
}
